import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { rgba } from 'polished';
import { TableCell, TableRow } from '@this/src/components/shared/ui/data_displays/table';
import type JalBookingInventoryItem from '@this/src/domain/arrangement/jal_booking_inventory_item';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import { Button } from '@this/components/shared/ui/inputs/button';
import { OrderItemStatusForm } from '@this/components/arrangement/shared/price_change_form';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox';
import { Fetcher } from '@this/src/util';
import InventoryCommentModal from '@this/components/arrangement/todo_list/auto_reservation_inventory/inventory_comment_modal';

interface Props {
  item: JalBookingInventoryItem;
  checked: boolean;
  onInventory: () => void;
  onToggle: (checked: boolean) => void;
}

const ItemsResultTableRow = observer(({ item, checked, onInventory, onToggle }: Props) => {
  const [showLogModal, setShowLogModal] = useState<boolean>(false);

  const handleInventory = async () => {
    const jalBookingInventoryPairId = item.jalBookingInventoryPairId;
    if (!jalBookingInventoryPairId) return;

    await Fetcher.put(
      `/arrangement/auto_reservation_inventory/jal_booking/items/${jalBookingInventoryPairId}/inventory.json`,
      {}
    );
    onInventory();
  };

  const handleCancelInventory = async () => {
    const jalBookingInventoryPairId = item.jalBookingInventoryPairId;
    if (!jalBookingInventoryPairId) return;
    await Fetcher.put(
      `/arrangement/auto_reservation_inventory/jal_booking/items/${jalBookingInventoryPairId}/cancel_inventory.json`,
      {}
    );
    onInventory();
  };

  return (
    <TableRow
      style={{
        background:
          !item.jalBookingInventoryPairJalReservationId || !item.jalBookingInventoryPairAiOperatorReservationId
            ? rgba('red', 0.3)
            : 'auto'
      }}
    >
      <TableCell>
        {/* 一括棚卸用チェック */}
        <Checkbox
          checked={checked}
          disabled={!!item.jalBookingInventoryPairInventoriedAt}
          onChange={e => onToggle(e.target.checked)}
        />
      </TableCell>
      <TableCell>
        {/* ID */}
        {item.aiOperatorReservationId || '　'}
        <br />
        {item.jalReservationId || '　'}
      </TableCell>
      <TableCell>
        {/* 作成日時 */}
        {item.aiOperatorReservationCreatedAt?.format('YYYY-MM-DD HH:mm') || '　'}
        <br />
        {item.jalReservationCreatedAt?.format('YYYY-MM-DD HH:mm') || '　'}
      </TableCell>
      <TableCell>
        {/* 予約指示ステータス */}
        {item.aiOperatorReservationStatus}
      </TableCell>
      <TableCell>
        {/* 予約番号 */}
        {item.reservationNumber}
      </TableCell>
      <TableCell>
        {/* 搭乗日 */}
        {item.targetDate?.format('YYYY-MM-DD')}
      </TableCell>
      <TableCell>
        {/* 便名 */}
        {item.flightName}
      </TableCell>
      <TableCell>
        {/* 予約金額 */}
        {item.price?.toLocaleString()}
      </TableCell>
      <TableCell>
        {/* 予約日 */}
        {item.reservationDate?.format('YYYY-MM-DD')}
      </TableCell>
      <TableCell>
        {/* 完全自動 */}
        {item.aiOperatorReservationIsAutoProcessed ? '○' : ''}
      </TableCell>
      <TableCell>
        {/* 予約ステータス */}
        {item.aiOperatorOrderItemId && item.aiOperatorJobType && item.aiOperatorStatus && (
          <OrderItemStatusForm
            orderItemId={item.aiOperatorOrderItemId}
            jobType={item.aiOperatorJobType}
            status={item.aiOperatorStatus}
            onJobTypeChange={value => {
              item.aiOperatorJobType = value;
            }}
            onStatusChange={value => {
              item.aiOperatorStatus = value;
            }}
            showLabel={false}
            fetchData={handleInventory}
          />
        )}
        {item.jalOrderItemId && item.jalJobType && item.jalStatus && (
          <OrderItemStatusForm
            orderItemId={item.jalOrderItemId}
            jobType={item.jalJobType}
            status={item.jalStatus}
            onJobTypeChange={value => {
              item.jalJobType = value;
            }}
            onStatusChange={value => {
              item.jalStatus = value;
            }}
            showLabel={false}
            fetchData={handleInventory}
          />
        )}
      </TableCell>
      <TableCell>
        {/* 棚卸済 */}
        {item.aiOperatorIsInventoried || '　'}
        <br />
        {item.jalIsInventoried || '　'}
      </TableCell>
      <TableCell>
        {/* 棚卸者 */}
        {item.aiOperatorInventoriedBy || '　'}
        <br />
        {item.jalInventoriedBy || '　'}
      </TableCell>
      <TableCell>
        {/* 棚卸日時 */}
        {item.aiOperatorInventoriedAt?.format('YYYY-MM-DD HH:mm') || '　'}
        <br />
        {item.jalInventoriedAt?.format('YYYY-MM-DD HH:mm') || '　'}
      </TableCell>
      <TableCell>
        {/* Trip ID */}
        <Link
          href={`/arrangement/virtual_counter?trip_id=${item.aiOperatorTripId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.aiOperatorTripId || '　'}
        </Link>
        <br />
        <Link
          href={`/arrangement/virtual_counter?trip_id=${item.jalTripId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.jalTripId || '　'}
        </Link>
      </TableCell>
      <TableCell>
        {/* OrderItem ID */}
        {item.aiOperatorOrderItemId || '　'}
        <br />
        {item.jalOrderItemId || '　'}
      </TableCell>
      <TableCell>
        {/* Trace ID */}
        {item.aiOperatorTraceId || '　'}
        <br />
        {item.jalTraceId || '　'}
      </TableCell>
      <TableCell>
        {/* コメント */}
        {item.jalBookingInventoryPairId && (
          <p>
            (<Link onClick={() => setShowLogModal(true)}>コメント</Link>)
          </p>
        )}
      </TableCell>
      <TableCell>
        {/* 棚卸操作 */}
        {item.jalBookingInventoryPairId && item.jalBookingInventoryPairInventoriedAt ? (
          <Button size="small" onClick={() => handleCancelInventory()}>
            取消
          </Button>
        ) : (
          <Button size="small" onClick={() => handleInventory()}>
            棚卸完了
          </Button>
        )}
      </TableCell>
      {showLogModal && (item.jalTraceId || item.aiOperatorTraceId) && (
        <InventoryCommentModal
          inventoryPairId={item.jalBookingInventoryPairId}
          onClose={() => setShowLogModal(false)}
        />
      )}
    </TableRow>
  );
});

export default ItemsResultTableRow;
